import React, { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  PasswordField,
  UserFormFields,
  validateUserFields,
  type UserFormErrors,
  type UserFormValues
} from './UserFormFields'

interface CreateUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (userData: UserFormData) => void
  loading?: boolean
}

interface UserFormData extends UserFormValues {
  password: string
}

const EMPTY_FORM: UserFormData = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  role: 'ASSISTENTE',
  isActive: true,
  oabNumber: '',
  oabState: ''
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const [formData, setFormData] = useState<UserFormData>(EMPTY_FORM)
  const [errors, setErrors] = useState<UserFormErrors>({})

  const validateForm = (): boolean => {
    const newErrors = validateUserFields(formData)

    if (!formData.password.trim()) {
      newErrors.password = 'Senha é obrigatória'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleReset = () => {
    setFormData(EMPTY_FORM)
    setErrors({})
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (validateForm()) {
      onSubmit(formData)
      handleReset()
    }
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) handleClose() }}>
      <DialogContent size="md" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Novo Usuário</DialogTitle>
          <DialogDescription>Cadastre uma pessoa da equipe com acesso ao sistema.</DialogDescription>
        </DialogHeader>

        <form id="create-user-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <UserFormFields
            idPrefix="create-user"
            values={formData}
            errors={errors}
            onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            passwordSlot={
              <PasswordField
                id="create-user-password"
                label="Senha *"
                value={formData.password}
                placeholder="Digite a senha"
                error={errors.password}
                onChange={(password) => setFormData((prev) => ({ ...prev, password }))}
              />
            }
          />
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="create-user-form" disabled={loading}>
            {loading && <Loader2 className="size-4 animate-spin" />}
            {loading ? 'Criando...' : 'Criar Usuário'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
